import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  acknowledgeFeatureAnnouncement,
  fetchFeatureAnnouncementAcknowledgements,
} from "../api/featureAnnouncements";
import { useSettings } from "../contexts/settings-context";
import { ensureLanguageLoaded } from "../../i18n";
import { setCurrentUser } from "../slices/session";
import { updateUserAsync } from "../slices/user";
import { FeatureAnnouncementDialog } from "./FeatureAnnouncementDialog";
import { alreadyAnsweredByLanguage, pendingAnnouncements } from "./registry";

// Answers whose POST failed (offline, deploy, 5xx). They count as read on this
// device and are sent again on the next load, so the dialog never nags twice.
const UNSENT_KEY = "promang.featureAnnouncements.unsent";

function readUnsent(userId) {
  try {
    const all = JSON.parse(window.localStorage.getItem(UNSENT_KEY) || "{}");
    const rows = all?.[userId];
    return Array.isArray(rows) ? rows.filter((row) => row && typeof row.key === "string") : [];
  } catch {
    return [];
  }
}

function writeUnsent(userId, rows) {
  try {
    const all = JSON.parse(window.localStorage.getItem(UNSENT_KEY) || "{}") || {};
    if (rows.length) all[userId] = rows;
    else delete all[userId];
    window.localStorage.setItem(UNSENT_KEY, JSON.stringify(all));
  } catch {
    // Storage blocked: the answer is only lost if the POST also failed.
  }
}

async function sendAcknowledgement(userId, key, response) {
  try {
    await acknowledgeFeatureAnnouncement(key, response);
    writeUnsent(userId, readUnsent(userId).filter((row) => row.key !== key));
    return true;
  } catch {
    const rest = readUnsent(userId).filter((row) => row.key !== key);
    writeUnsent(userId, [...rest, { key, response }]);
    return false;
  }
}

/** Shows unread product announcements ("Ny funksjon") one at a time after login. */
export function FeatureAnnouncementsHost() {
  const userId = useSelector((state) => state.session.currentUser?.id);
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  if (!userId || !companyId) return null;
  return <FeatureAnnouncementsQueue key={userId} userId={userId} />;
}

function FeatureAnnouncementsQueue({ userId }) {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { settings, saveSettings } = useSettings();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [queue, setQueue] = useState([]);
  const [shown, setShown] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Retry answers that did not reach the server last time.
      const unsent = readUnsent(userId);
      for (const row of unsent) await sendAcknowledgement(userId, row.key, row.response);
      const answeredHere = unsent.map((row) => row.key);
      let rows;
      try {
        rows = await fetchFeatureAnnouncementAcknowledgements();
      } catch {
        // Unknown read state (offline, training gate, old API): show nothing rather than repeat old news.
        return;
      }
      if (cancelled) return;
      const acknowledgedKeys = new Set([
        ...rows.map((row) => row.key),
        ...answeredHere,
        ...readUnsent(userId).map((row) => row.key),
      ]);
      const currentLanguage = i18n.language;
      for (const item of alreadyAnsweredByLanguage({ acknowledgedKeys, currentLanguage })) {
        sendAcknowledgement(userId, item.key, "switched_language");
      }
      const pending = pendingAnnouncements({ acknowledgedKeys, currentLanguage, user: currentUser });
      // A language prompt is written partly in the new language; skip it this time if that bundle cannot load.
      const ready = await Promise.all(
        pending.map((item) =>
          item.type === "language"
            ? ensureLanguageLoaded(item.language.code).then(() => true, () => false)
            : true,
        ),
      );
      if (!cancelled) setQueue(pending.filter((_, index) => ready[index]));
    };
    load();
    return () => {
      cancelled = true;
    };
    // Load once per user session; language/user changes while open must not reshuffle the queue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const finish = useCallback(
    (announcement, response) => {
      setQueue((items) => items.filter((item) => item.key !== announcement.key));
      setShown((count) => count + 1);
      sendAcknowledgement(userId, announcement.key, response);
    },
    [userId],
  );

  const switchLanguage = useCallback(
    async (announcement) => {
      const { language } = announcement;
      const result = await dispatch(updateUserAsync({ id: userId, locale: language.apiLocale }));
      if (result?.error || !result?.payload) throw new Error("language_update_failed");
      dispatch(setCurrentUser({ ...currentUser, locale: language.apiLocale }));
      await saveSettings({ ...settings, language: language.code });
      finish(announcement, "switched_language");
    },
    [currentUser, dispatch, finish, saveSettings, settings, userId],
  );

  const current = queue[0];
  if (!current) return null;
  return (
    <FeatureAnnouncementDialog
      key={current.key}
      announcement={current}
      position={shown + 1}
      total={shown + queue.length}
      currentLanguage={i18n.language}
      onDismiss={(item) => finish(item, "dismissed")}
      onConfirm={finish}
      onSwitchLanguage={switchLanguage}
    />
  );
}
